import type { PlayerRow } from 'apps/scripts/comparison-tool/components/utils/types'

export const pitchTypeCategories: string[] = [
    'pitch_run_value',
    'pitch_velo',
    'pitch_usage',
    'pitch_spin',
    'pitch_whiff_percent',
    'pitch_break_x',
    'pitch_break_z',
    'pitch_break_z_induced',
    'pitch_extension',
]

export const noColorCategories: string[] = [
    'pitch_usage',
    'pitch_spin',
    'pitch_break_x',
    'pitch_break_z',
    'pitch_break_z_induced',
]

export const pitchTypes: string[] = [
    'ff',
    'si',
    'fc',
    'ch',
    'fs',
    'fo',
    'sc',
    'cu',
    'sl',
    'st',
    'sv',
    'kn',
]

// const armsidePitchTypes: string[] = ['ff', 'si', 'ch', 'fs', 'fo', 'sc']
const dropPitchTypes: string[] = [
    'si',
    'fc',
    'ch',
    'fs',
    'fo',
    'sc',
    'cu',
    'sl',
    'st',
    'sv',
]

export const pitchArsenalStats: (keyof PlayerRow)[] = pitchTypes
    .map((type) => {
        const pitchPctArr: (keyof PlayerRow)[] = []
        pitchTypeCategories.forEach((category) => {
            pitchPctArr.push(
                `${type}_percent_rank_${category}` as keyof PlayerRow
            )
        })
        return pitchPctArr
    })
    .flat()

export const allPossibleStats: (keyof PlayerRow)[] = [
    'percent_rank_swing_take_run_value',
    'percent_rank_fielding_run_value',
    'percent_rank_runner_run_value',
    'percent_rank_pitch_run_value_fastball',
    'percent_rank_pitch_run_value_breaking',
    'percent_rank_pitch_run_value_offspeed',
    'percent_rank_xwoba',
    'percent_rank_xera',
    'percent_rank_woba',
    'percent_rank_xwobacon',
    'percent_rank_wobacon',
    'percent_rank_xba',
    'percent_rank_ba',
    'percent_rank_xobp',
    'percent_rank_obp',
    'percent_rank_xslg',
    'percent_rank_slg',
    'percent_rank_bacon',
    'percent_rank_xbacon',
    'percent_rank_babip',
    'percent_rank_xiso',
    'percent_rank_iso',
    'percent_rank_exit_velocity_avg',
    'percent_rank_exit_velocity_max',
    'percent_rank_distance_hr_avg',
    'percent_rank_xhr',
    'percent_rank_barrel',
    'percent_rank_barrel_batted_rate',
    'percent_rank_launch_angle_avg',
    'percent_rank_hard_hit_percent',
    'percent_rank_sweet_spot_percent',
    'percent_rank_swing_speed',
    'percent_rank_swing_length',
    'percent_rank_squared_up_swing',
    'percent_rank_blasts_swing',
    'percent_rank_chase_percent',
    'percent_rank_whiff_percent',
    'percent_rank_k_percent',
    'percent_rank_bb_percent',
    'percent_rank_groundballs_percent',
    'percent_rank_airballs_percent',
    'percent_rank_pull_percent_airballs',
    'percent_rank_avg_hyper_speed',
    'percent_rank_avg_best_speed',
    'percent_rank_attack_angle',
    'percent_rank_vertical_swing_path',
    // 'percent_rank_acceleration',
    'percent_rank_ideal_angle_rate',
    'percent_rank_oaa',
    'percent_rank_fielding_run_value_arm',
    'percent_rank_arm_overall',
    'percent_rank_arm_max',
    'percent_rank_jump',
    'percent_rank_blocks_above_average',
    'percent_rank_cs_above_average',
    'percent_rank_fielding_run_value_framing',
    'percent_rank_pop_2b',
    'percent_rank_arm_cs_2b',
    'percent_rank_speed_order',
    'percent_rank_runner_runs_sb',
    'percent_rank_runner_runs_xb',
    ...pitchArsenalStats,
]

const validSplitKeys = new Set(
    allPossibleStats.map((key) => key.replace('percent_rank_', ''))
)

function isPlayerRowKey(s: string): s is keyof PlayerRow {
    return validSplitKeys.has(s)
}

export function mapPercentileNames(d: string): keyof PlayerRow {
    if (
        d.split('percent_rank').length > 0 &&
        d.split('percent_rank')[0] !== ''
    ) {
        const [pitch, stat] = d.split('_percent_rank_')
        return `${pitch}_${stat}` as keyof PlayerRow
    }

    const splitKey = d.split('percent_rank_')[1]

    switch (splitKey) {
        case 'chase_percent':
            return 'oz_swing_percent'
        case 'oaa':
            return 'outs_above_average'
        case 'framing':
            return 'fielding_run_value_framing'
        case 'jump':
            return 'jump_v_avg'
        case 'cu_spin':
            return 'curveball_spin'
        case 'arm_max':
            return 'max_arm_strength'
        case 'swing_speed':
            return 'avg_swing_speed'
        case 'swing_length':
            return 'avg_swing_length'
        case 'speed_order':
            return 'sprint_speed'
        default:
            if (isPlayerRowKey(splitKey)) {
                return splitKey
            }

            throw new Error(`Unknown percentile key: ${d}`)
    }
}

export const threeDigitTypes = [
    'ba',
    'xba',
    'est_ba',
    'obp',
    'xobp',
    'est_obp',
    'slg',
    'xslg',
    'est_slg',
    'iso',
    'xiso',
    'est_iso',
    'ops',
    'babip',
    'est_babip',
    'woba',
    'xwoba',
    'est_woba',
    'wobacon',
    'xwobacon',
    'est_wobacon',
    'bacon',
    'xbacon',
    'est_bacon',
]

export const pitchingRunTypes = [
    'pitch_run_value_fastball',
    'pitch_run_value_breaking',
    'pitch_run_value_offspeed',
]

export const guffTypes = [
    'avg_hyper_speed',
    'avg_best_speed',
    'era',
    'xera',
    'k_percent',
    'bb_percent',
    'exit_velocity_avg',
    'exit_velocity_max',
    'whiff_percent',
    'chase_percent',
    'oz_swing_percent',
    'launch_angle_avg',
    'groundballs_percent',
    'airballs_percent',
    'pull_percent_airballs',
    'attack_angle',
    'vertical_swing_path',
    // 'acceleration',
    'ideal_angle_rate',
    'arm_overall',
    'arm_max',
    'max_arm_strength',
    'jump',
    'xhr',
    'sprint_speed',
    'pop_2b',
    'arm_cs_2b',
    'avg_swing_speed',
    ...pitchTypeCategories,
]

export const flipGoodTypesBatter = [
    'percent_rank_k_percent',
    'percent_rank_whiff_percent',
    'percent_rank_chase_percent',
    'percent_rank_groundballs_percent',
    // 'percent_rank_swing_length',
    // 'percent_rank_vertical_swing_path',
    'percent_rank_pop_2b',
    ...pitchTypes.map((type) => `${type}_percent_rank_pitch_whiff_percent`),
]

export const flipGoodTypesPitcher = [
    'percent_rank_xera',
    'percent_rank_xwoba',
    'percent_rank_woba',
    'percent_rank_xwobacon',
    'percent_rank_wobacon',
    'percent_rank_xba',
    'percent_rank_ba',
    'percent_rank_xobp',
    'percent_rank_obp',
    'percent_rank_xslg',
    'percent_rank_slg',
    'percent_rank_bacon',
    'percent_rank_xbacon',
    'percent_rank_babip',
    'percent_rank_xiso',
    'percent_rank_iso',
    'percent_rank_exit_velocity_avg',
    'percent_rank_exit_velocity_max',
    'percent_rank_distance_hr_avg',
    'percent_rank_xhr',
    'percent_rank_barrel',
    'percent_rank_barrel_batted_rate',
    'percent_rank_launch_angle_avg',
    'percent_rank_hard_hit_percent',
    'percent_rank_sweet_spot_percent',
    'percent_rank_swing_speed',
    'percent_rank_squared_up_swing',
    'percent_rank_bb_percent',
    'percent_rank_airballs_percent',
    'percent_rank_pull_percent_airballs',
    'percent_rank_avg_hyper_speed',
    'percent_rank_avg_best_speed',
    'percent_rank_attack_angle',
    'percent_rank_swing_length',
    'percent_rank_vertical_swing_path',
    // 'percent_rank_acceleration',
    'percent_rank_ideal_angle_rate',
    'percent_rank_blasts_swing',
    // ...armsidePitchTypes.map((type) => `${type}_percent_rank_pitch_break_x`),
    // ...dropPitchTypes.map((type) => `${type}_percent_rank_pitch_break_z`),
    ...dropPitchTypes.map(
        (type) => `${type}_percent_rank_pitch_break_z_induced`
    ),
]

export const DEFAULT_BATTING_STATS: (keyof PlayerRow)[] = [
    'percent_rank_swing_take_run_value',
    'percent_rank_runner_run_value',
    'percent_rank_fielding_run_value',
    'percent_rank_xwoba',
    'percent_rank_xba',
    'percent_rank_xslg',
    'percent_rank_exit_velocity_avg',
    'percent_rank_barrel_batted_rate',
    'percent_rank_hard_hit_percent',
    'percent_rank_sweet_spot_percent',
    'percent_rank_swing_speed',
    'percent_rank_squared_up_swing',
    'percent_rank_chase_percent',
    'percent_rank_whiff_percent',
    'percent_rank_k_percent',
    'percent_rank_bb_percent',
    'percent_rank_oaa',
    'percent_rank_fielding_run_value_arm',
    'percent_rank_arm_overall',
    'percent_rank_speed_order',
]

export const DEFAULT_PITCHING_STATS: (keyof PlayerRow)[] = [
    'percent_rank_swing_take_run_value',
    'percent_rank_pitch_run_value_fastball',
    'percent_rank_pitch_run_value_breaking',
    'percent_rank_pitch_run_value_offspeed',
    'percent_rank_xera',
    'percent_rank_xba',
    'percent_rank_xslg',
    'percent_rank_chase_percent',
    'percent_rank_whiff_percent',
    'percent_rank_k_percent',
    'percent_rank_bb_percent',
    'percent_rank_exit_velocity_avg',
    'percent_rank_barrel_batted_rate',
    'percent_rank_hard_hit_percent',
    'percent_rank_sweet_spot_percent',
    'percent_rank_groundballs_percent',
    'ff_percent_rank_pitch_velo',
    'ff_percent_rank_pitch_run_value',
    'fc_percent_rank_pitch_run_value',
    'si_percent_rank_pitch_run_value',
    'sl_percent_rank_pitch_run_value',
    'st_percent_rank_pitch_run_value',
    'ch_percent_rank_pitch_run_value',
    'fs_percent_rank_pitch_run_value',
    'cu_percent_rank_pitch_run_value',
]

export const DEFAULT_CATCHING_STATS: (keyof PlayerRow)[] = [
    'percent_rank_swing_take_run_value',
    'percent_rank_runner_run_value',
    'percent_rank_fielding_run_value',
    'percent_rank_xwoba',
    'percent_rank_xba',
    'percent_rank_xslg',
    'percent_rank_exit_velocity_avg',
    'percent_rank_barrel_batted_rate',
    'percent_rank_hard_hit_percent',
    'percent_rank_sweet_spot_percent',
    'percent_rank_swing_speed',
    'percent_rank_squared_up_swing',
    'percent_rank_chase_percent',
    'percent_rank_whiff_percent',
    'percent_rank_k_percent',
    'percent_rank_bb_percent',
    'percent_rank_blocks_above_average',
    'percent_rank_cs_above_average',
    'percent_rank_fielding_run_value_framing',
    'percent_rank_pop_2b',
    'percent_rank_arm_cs_2b',
    'percent_rank_speed_order',
]

export const noColorTypes: (keyof PlayerRow)[] = [
    'percent_rank_swing_length',
    'percent_rank_attack_angle',
    'percent_rank_vertical_swing_path',
    ...pitchTypes.flatMap((type) =>
        noColorCategories.map(
            (category) => `${type}_percent_rank_${category}` as keyof PlayerRow
        )
    ),
]
